import { useState, useRef, useCallback, useEffect } from "react";

export function usePickerPositioning({ timerInput }) {
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [timePickerPosition, setTimePickerPosition] = useState(null);
  const [isAmountPickerOpen, setIsAmountPickerOpen] = useState(false);
  const [amountPickerPosition, setAmountPickerPosition] = useState(null);

  const timePickerTriggerRef = useRef(null);
  const timePickerPanelRef = useRef(null);
  const timePickerScrollRef = useRef(null);
  const selectedTimeOptionRef = useRef(null);

  const amountPickerTriggerRef = useRef(null);
  const amountPickerPanelRef = useRef(null);

  const updateTimePickerPosition = useCallback(() => {
    const trigger = timePickerTriggerRef.current;
    const panel = timePickerPanelRef.current;

    if (!trigger || typeof window === "undefined") {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const isMobileSheet = window.innerWidth < 640;

    if (isMobileSheet) {
      setTimePickerPosition({ isMobileSheet: true });
      return;
    }

    const pickerWidth = Math.max(rect.width, 130);
    const pickerHeight = panel ? panel.getBoundingClientRect().height : 80;
    const viewportPadding = 12;

    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      window.innerWidth - pickerWidth - viewportPadding,
    );

    const spaceAbove = rect.top;
    const top =
      spaceAbove > pickerHeight + 12
        ? rect.top - pickerHeight - 4
        : rect.bottom + 4;

    setTimePickerPosition({
      isMobileSheet: false,
      top,
      left,
      width: pickerWidth,
    });
  }, []);

  const openTimePicker = useCallback(() => {
    updateTimePickerPosition();
    setIsTimePickerOpen(true);
  }, [updateTimePickerPosition]);

  const closeTimePicker = useCallback(() => {
    setIsTimePickerOpen(false);
  }, []);

  useEffect(() => {
    if (!isTimePickerOpen) {
      return undefined;
    }

    updateTimePickerPosition();

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        timePickerPanelRef.current?.contains(target) ||
        timePickerTriggerRef.current?.contains(target)
      ) {
        return;
      }
      closeTimePicker();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeTimePicker();
      }
    };

    window.addEventListener("resize", updateTimePickerPosition);
    window.addEventListener("scroll", updateTimePickerPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", updateTimePickerPosition);
      window.removeEventListener("scroll", updateTimePickerPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeTimePicker, isTimePickerOpen, updateTimePickerPosition]);

  useEffect(() => {
    if (!isTimePickerOpen) {
      return;
    }

    requestAnimationFrame(() => {
      selectedTimeOptionRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    });
  }, [isTimePickerOpen, timerInput]);

  const updateAmountPickerPosition = useCallback(() => {
    const trigger = amountPickerTriggerRef.current;
    const panel = amountPickerPanelRef.current;

    if (!trigger || typeof window === "undefined") {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const isMobileSheet = window.innerWidth < 640;

    if (isMobileSheet) {
      setAmountPickerPosition({ isMobileSheet: true });
      return;
    }

    const pickerWidth = Math.max(rect.width, 130);
    const pickerHeight = panel ? panel.getBoundingClientRect().height : 80;
    const viewportPadding = 12;

    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      window.innerWidth - pickerWidth - viewportPadding,
    );

    const spaceAbove = rect.top;
    const top =
      spaceAbove > pickerHeight + 12
        ? rect.top - pickerHeight - 4
        : rect.bottom + 4;

    setAmountPickerPosition({
      isMobileSheet: false,
      top,
      left,
      width: pickerWidth,
    });
  }, []);

  const openAmountPicker = useCallback(() => {
    updateAmountPickerPosition();
    setIsAmountPickerOpen(true);
  }, [updateAmountPickerPosition]);

  const closeAmountPicker = useCallback(() => {
    setIsAmountPickerOpen(false);
  }, []);

  useEffect(() => {
    if (!isAmountPickerOpen) {
      return undefined;
    }

    updateAmountPickerPosition();

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        amountPickerPanelRef.current?.contains(target) ||
        amountPickerTriggerRef.current?.contains(target)
      ) {
        return;
      }
      closeAmountPicker();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAmountPicker();
      }
    };

    window.addEventListener("resize", updateAmountPickerPosition);
    window.addEventListener("scroll", updateAmountPickerPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", updateAmountPickerPosition);
      window.removeEventListener("scroll", updateAmountPickerPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeAmountPicker, isAmountPickerOpen, updateAmountPickerPosition]);

  return {
    isTimePickerOpen,
    setIsTimePickerOpen,
    timePickerPosition,
    timePickerTriggerRef,
    timePickerPanelRef,
    timePickerScrollRef,
    selectedTimeOptionRef,
    openTimePicker,
    closeTimePicker,
    isAmountPickerOpen,
    setIsAmountPickerOpen,
    amountPickerPosition,
    amountPickerTriggerRef,
    amountPickerPanelRef,
    openAmountPicker,
    closeAmountPicker,
  };
}
